import { tryOnMounted } from '@vueuse/core'
import { storeToRefs } from 'pinia'

import dayjs from 'dayjs'
import { usePlatformStore } from '@/store-pinia/platform'
import { loadScript } from '@/utils/dom-loader'
import { FacebookDialogLogin } from '@/utils/facebook-login'
import { adaptGenderCode } from '@/utils/utils'
import * as loginService from '@/service/login'
import { ResponseCode } from '~/view-models/common'
import { useThirdLogin } from '@/composables/useThirdLogin'

/**
 * ! for component only
 */
export function useLogin() {
  const { isShowLoginStep } = storeToRefs(usePlatformStore())
  const { $store: store } = useNuxtApp()
  /**
   * * 純開 login，沒有前面 landing 畫面的步驟
   */
  function openPureLogin() {
    isShowLoginStep.value = true
    store.dispatch('dialog/setLoginDialog', { state: true })
  }

  function closeLogin() {
    isShowLoginStep.value = false
    store.dispatch('dialog/setLoginDialog', { state: false })
  }

  return {
    openPureLogin,
    closeLogin,
  }
}

export function useFBLogin() {
  const config = useRuntimeConfig()
  const { $axios, $store: store } = useNuxtApp()

  // Shared logic delegated to useThirdLogin
  const { showConsent, pendingUserInfo, onLoginSuccess, onNeedRegister, handleConsentConfirm, openErrorDialog, resetConsent } = useThirdLogin({
    provider: 'facebook',
    registerOptions: { byFacebook: true },
  })

  tryOnMounted(() => {
    loadScript(() => FacebookDialogLogin.init(config.public.FB_APP_ID), {
      id: 'fbLogin',
      src: 'https://connect.facebook.net/en_US/sdk.js',
      defer: true,
    })
  })

  async function login() {
    try {
      const { accessToken, userID } = await FacebookDialogLogin.login()
      const isLogin = await loginFunNow(accessToken, userID)
      if (!isLogin) {
        return false
      }
      await store.dispatch('authentication/getUserInfo')
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  /**
   *
   * @param {string} accessToken
   * @param {string} fbid
   * @returns {Promise<boolean>} Promise<boolean>
   */
  async function loginFunNow(accessToken, fbid) {
    try {
      const loginResult = await loginService.facebookLogin($axios, { fbToken: accessToken, id: fbid })
      onLoginSuccess(loginResult.data.uid)
      return true
    } catch (error) {
      console.error('fbLogin: error', error)
      if (error.code === ResponseCode.TokenNotFound) {
        await preparePendingUserInfo(accessToken)
        return false
      }
      openErrorDialog(error.message)
      return false
    }
  }

  /**
   * Prepare pending user info and show consent modal
   * @param {string} accessToken
   * @returns {Promise<void>}
   */
  async function preparePendingUserInfo(accessToken) {
    try {
      const fbProfile = await FacebookDialogLogin.getProfile()
      onNeedRegister({
        email: fbProfile.email,
        name: fbProfile.name,
        gender: adaptGenderCode(fbProfile.gender),
        birthday: fbProfile.birthday ? dayjs(fbProfile.birthday).format('YYYY-MM-DD') : '',
        fbId: fbProfile.id,
        fbToken: accessToken,
      })
    } catch (error) {
      console.error('preparePendingUserInfo: error', error)
      openErrorDialog(error.message)
    }
  }

  return {
    login,
    showConsent,
    pendingUserInfo,
    handleConsentConfirm,
    resetConsent,
  }
}
